import { memo, useCallback, useEffect, useRef } from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";

import { Dialog, Transition } from "@headlessui/react";

import { Button } from "@components/Button";
import { Heading } from "@components/Heading";
import { Icon } from "@components/Icon";
import { Text } from "@components/Text";
import { Dropdown } from "@react/components/Dropdown";
import type { DropdownOption } from "@react/components/Dropdown";
import { COUNTRY_FLAG_ICONS } from "@root/tablescape/constants";
import { type TTailwindString, tw } from "classnames";

export type CountrySelectorModalRootViewProps = {
  countryOptions: DropdownOption[];
  initialCountry: DropdownOption;
  initialLanguage: DropdownOption;
  isOpen: boolean;
  languageOptions: DropdownOption[];
  onClose: () => void;
  onContinue: () => void;
  onSelectCountry: (option: DropdownOption) => void;
  onSelectLanguage: (option: DropdownOption) => void;
  text: {
    closeAlt: string;
    continue: string;
    countryLabel: string;
    languageLabel: string;
    subtitle: string;
    title: string;
  };
};

// Narrow unknown values to valid COUNTRY_FLAG_ICONS keys
const isCountryFlagKey = (
  value: unknown
): value is keyof typeof COUNTRY_FLAG_ICONS =>
  typeof value === "string" && value in COUNTRY_FLAG_ICONS;

const transitionAnimation = {
  backdrop: {
    enter: tw("transition-opacity", "ease-linear", "duration-300"),
    enterFrom: tw("opacity-0"),
    enterTo: tw("opacity-80"),
    leave: tw("transition-opacity", "ease-linear", "duration-300"),
    leaveFrom: tw("opacity-80"),
    leaveTo: tw("opacity-0"),
  },
  modal: {
    enter: tw("transition", "ease-in-out", "duration-300"),
    enterFrom: tw("opacity-0", "translate-y-1/2"),
    enterTo: tw("opacity-100", "translate-y-0"),
    leave: tw("transition", "ease-in-out", "duration-300"),
    leaveFrom: tw("opacity-100", "translate-y-0"),
    leaveTo: tw("opacity-0", "translate-y-1/2"),
  },
};

const styles = {
  root: tw(
    "top-0",
    "left-0",
    "right-0",
    "bottom-0",
    "fixed",
    "inset-0",
    "z-1000",
    "flex",
    "items-center",
    "justify-center"
  ),
  backdrop: tw(
    "absolute",
    "inset-0",
    "h-screen",
    "w-full",
    "bg-overlay",
    "opacity-80"
  ),
  dialog: tw(
    "box-border",
    "relative",
    "z-1000",
    "w-full",
    "mx-4",
    "my-4",
    "overflow-visible",
    "max-h-[calc(100vh-2rem)]" as TTailwindString,
    "md:mx-0",
    "md:my-8",
    "md:max-h-[calc(100vh-4rem)]" as TTailwindString,
    "md:max-w-[648px]" as TTailwindString,
    "bg-background-primary",
    "px-6",
    "py-10",
    "md:px-16",
    "md:py-20"
  ),
  dialogContent: tw(
    "flex",
    "flex-col",
    "items-center",
    "text-center",
    "gap-1",
    "text-balance" as TTailwindString
  ),
  container: tw(
    "mx-auto",
    "flex",
    "md:max-w-[507px]" as TTailwindString,
    "flex-col",
    "items-center",
    "gap-6",
    "md:gap-9"
  ),
  closeButton: tw(
    "absolute",
    "top-3",
    "right-3",
    "md:top-6",
    "md:right-6",
    "p-0",
    "border-0",
    "bg-transparent",
    "cursor-pointer"
  ),
  fields: tw(
    "flex",
    "w-full",
    "flex-col",
    "items-stretch",
    "md:items-center",
    "gap-4",
    "md:max-w-[300px]" as TTailwindString
  ),
  field: tw("w-full"),
};

const CountrySelectorModalRootViewBase = ({
  countryOptions,
  initialCountry,
  initialLanguage,
  isOpen,
  languageOptions,
  onClose,
  onContinue,
  onSelectCountry,
  onSelectLanguage,
  text,
}: CountrySelectorModalRootViewProps) => {
  // Track the trigger element to restore focus when modal closes
  const lastActiveRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (isOpen) {
      lastActiveRef.current = (document.activeElement as HTMLElement) ?? null;
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    onClose();
    // Restore focus to previously focused element, if still in DOM
    if (lastActiveRef.current?.focus) {
      try {
        lastActiveRef.current.focus();
      } catch {
        // no-op
      }
    }
  }, [onClose]);

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      if (event.key === "Escape") handleClose();
    },
    [handleClose]
  );

  return (
    <Transition
      appear
      as={Dialog}
      className={styles.root}
      onClose={handleClose}
      show={isOpen}
    >
      {/* Backdrop */}
      <Transition.Child {...transitionAnimation.backdrop}>
        <div
          aria-hidden="true"
          className={styles.backdrop}
          onClick={handleClose}
        />
      </Transition.Child>
      <Transition.Child
        aria-labelledby="country-selector-modal-title"
        as={Dialog.Panel}
        className={styles.dialog}
        {...transitionAnimation.modal}
      >
        <button
          aria-label={text.closeAlt}
          className={styles.closeButton}
          onClick={handleClose}
          type="button"
        >
          <span aria-hidden="true">
            <Icon name="close" />
          </span>
        </button>

        <div className={styles.container} onKeyDown={handleKeyDown}>
          <div className={styles.dialogContent}>
            <Dialog.Title as="div" id="country-selector-modal-title">
              <Heading as="h4" size={{ sm: "md", md: "md" }}>
                {text.title}
              </Heading>
            </Dialog.Title>
            <Dialog.Description as="div">
              <Text size={{ sm: "md", md: "lg" }}>{text.subtitle}</Text>
            </Dialog.Description>
          </div>

          <div className={styles.fields}>
            <div className={styles.field}>
              <Dropdown
                ariaLabel={text.countryLabel}
                fluid
                iconName={
                  isCountryFlagKey(initialCountry.value)
                    ? COUNTRY_FLAG_ICONS[initialCountry.value]
                    : undefined
                }
                onSelectOption={onSelectCountry}
                options={countryOptions}
                selectedOption={initialCountry}
              />
            </div>

            <div className={styles.field}>
              <Dropdown
                ariaLabel={text.languageLabel}
                fluid
                onSelectOption={onSelectLanguage}
                options={languageOptions}
                selectedOption={initialLanguage}
              />
            </div>

            <Button fluid onClick={onContinue}>
              {text.continue}
            </Button>
          </div>
        </div>
      </Transition.Child>
    </Transition>
  );
};

export const CountrySelectorModalRootView = memo(
  CountrySelectorModalRootViewBase
);
